import { useEffect, useState } from 'react'
import { FiArrowLeft, FiLoader } from 'react-icons/fi'
import axios from 'axios'
import toast from 'react-hot-toast'
import { useCart } from '../../context/CartContext'
import { isFormStylingApplied, isButtonStylingApplied } from '../../utils/sectionFormStyling'

export default function Checkout({ onBack, website, section, sectionFormStyling, useDefaultStyling = true, sectionType = 'checkout', customerProfile = null, customerAuthToken = null }) {
  const { cartItems, total, subtotal, clearCart } = useCart()
  const [loading, setLoading] = useState(false)
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zip: ''
  })
  const [errors, setErrors] = useState({})

  useEffect(() => {
    if (!customerProfile) return
    setFormData((prev) => ({
      ...prev,
      name: prev.name || `${customerProfile.firstName || ''} ${customerProfile.lastName || ''}`.trim(),
      email: prev.email || customerProfile.email || '',
      phone: prev.phone || customerProfile.phone || '',
      address: prev.address || customerProfile.shippingAddress || '',
      city: prev.city || customerProfile.shippingCity || '',
      state: prev.state || customerProfile.shippingState || '',
      zip: prev.zip || customerProfile.shippingZip || ''
    }))
  }, [customerProfile])

  const isFormStyling = useDefaultStyling || isFormStylingApplied(sectionFormStyling, sectionType, section?._id)
  const isButtonStyling = useDefaultStyling || isButtonStylingApplied(sectionFormStyling, sectionType, section?._id)
  
  // Default values to fallback to if styling is not provided
  const DEFAULT_FORM_COLORS = {
    backgroundColor: '#FFFFFF',
    textColor: '#111827',
    hoverBackgroundColor: '#F9FAFB',
    hoverTextColor: '#374151',
    buttonBackgroundColor: '#3B82F6',
    buttonTextColor: '#FFFFFF',
    hoverButtonBackgroundColor: '#2563eb',
    hoverButtonTextColor: '#ffffff'
  }
  
  // Ensure we have valid styling values
  const formColors = isFormStyling ? (sectionFormStyling || DEFAULT_FORM_COLORS) : undefined
  
  const formStyle = isFormStyling ? {
    backgroundColor: formColors?.backgroundColor || DEFAULT_FORM_COLORS.backgroundColor,
    color: formColors?.textColor || DEFAULT_FORM_COLORS.textColor
  } : undefined
  const labelStyle = isFormStyling ? { color: formColors?.textColor || DEFAULT_FORM_COLORS.textColor } : undefined
  const inputStyle = isFormStyling ? { 
    backgroundColor: formColors?.backgroundColor || DEFAULT_FORM_COLORS.backgroundColor,
    color: formColors?.textColor || DEFAULT_FORM_COLORS.textColor,
    borderColor: formColors?.textColor || DEFAULT_FORM_COLORS.textColor
  } : undefined
  const buttonStyle = isButtonStyling ? {
    backgroundColor: formColors?.buttonBackgroundColor || DEFAULT_FORM_COLORS.buttonBackgroundColor,
    color: formColors?.buttonTextColor || DEFAULT_FORM_COLORS.buttonTextColor
  } : { backgroundColor: '#3B82F6', color: '#FFFFFF' } // Always provide default button styling

  // Debug: Log website payment settings
  console.log('[Checkout] Website object:', {
    id: website?._id,
    slug: website?.slug,
    hasPaymentSettings: !!website?.paymentSettings,
    paymentSettings: website?.paymentSettings,
    paymentGateway: website?.paymentSettings?.paymentGateway
  })

  // Validate form
  const validateForm = () => {
    const newErrors = {}

    if (!formData.name.trim()) {
      newErrors.name = 'Full name is required'
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email address'
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required'
    }

    // Shipping fields validation
    if (!formData.address?.trim()) {
      newErrors.address = 'Address is required'
    }

    if (!formData.city?.trim()) {
      newErrors.city = 'City is required'
    }

    if (!formData.state?.trim()) {
      newErrors.state = 'State is required'
    }

    if (!formData.zip?.trim()) {
      newErrors.zip = 'ZIP code is required'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // Handle input change
  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }))
    }
  }

  // Handle checkout
  const handleCheckout = async (e) => {
    e.preventDefault()

    console.log('[Checkout] Form submitted', {
      cartItemsCount: cartItems.length,
      website: website?.publicUrl,
      formValid: validateForm()
    })

    // Validate cart
    if (cartItems.length === 0) {
      toast.error('Your cart is empty')
      return
    }

    // Validate that all items have the same currency
    const currencies = [...new Set(cartItems.map(item => item.currency))]
    if (currencies.length > 1) {
      toast.error('All items in your cart must have the same currency. Please remove items with different currencies.')
      return
    }

    const cartCurrency = currencies[0] || 'NGN'
    const paymentGateway = website?.paymentSettings?.paymentGateway || 'paystack'
    
    // Only show currency warning for Paystack (which defaults to NGN for unsupported currencies)
    if (paymentGateway === 'paystack') {
      const supportedCurrencies = ['NGN', 'USD', 'GHS', 'ZAR', 'KES']
      if (!supportedCurrencies.includes(cartCurrency)) {
        // Show warning but allow checkout (backend will default to NGN)
        try {
          toast(`Currency ${cartCurrency} is not supported for payment. Your payment will be processed in NGN.`, { icon: '⚠️', duration: 5000 })
        } catch (err) {
          console.error('Toast warning failed, falling back to alert', err)
          alert(`Currency ${cartCurrency} is not supported for payment. Your payment will be processed in NGN.`)
        }
      }
    }
    // For Stripe, backend will reject unsupported currencies with proper error message

    // Validate form
    if (!validateForm()) {
      toast.error('Please fill in all required fields')
      return
    }

    try {
      setLoading(true)

      // Prepare order payload
      const orderPayload = {
        items: cartItems.map(item => ({
          itemId: item._id,
          type: item.type || 'product',
          itemType: item.itemType || item.type || 'product',
          title: item.name,
          description: item.description || '',
          price: item.price,
          quantity: item.quantity
        })),
        email: formData.email,
        name: formData.name,
        phone: formData.phone,
        shipping: {
          address: formData.address,
          city: formData.city,
          state: formData.state,
          zip: formData.zip
        },
        amount: total,
        currency: cartCurrency
      }

      console.log('[Checkout] Order payload prepared:', {
        itemsCount: orderPayload.items.length,
        amount: orderPayload.amount,
        currency: orderPayload.currency,
        email: orderPayload.email,
        name: orderPayload.name
      })

      // Ensure we have a valid store slug before calling backend
      const storeSlug = website?.publicUrl
      if (!storeSlug) {
        console.error('[Checkout] Missing website.publicUrl, cannot proceed with checkout')
        toast.error('Unable to determine store information. Please reload the page and try again.')
        setLoading(false)
        return
      }

      const endpoint = `/store-payments/store/${storeSlug}/checkout`
      console.log('[Checkout] Sending request to:', endpoint)
      console.log('[Checkout] Full payload:', JSON.stringify(orderPayload, null, 2))

      // Initialize payment with backend
      console.log('[Checkout] Making axios POST request...')
      const response = await axios.post(
        endpoint,
        orderPayload,
        customerAuthToken ? { headers: { Authorization: `Bearer ${customerAuthToken}` } } : undefined
      )

      console.log('[Checkout] Response received successfully!')
      console.log('[Checkout] Payment initialization response:', {
        status: response.status,
        statusText: response.statusText,
        success: response.data.success,
        message: response.data.message,
        gateway: website?.paymentSettings?.paymentGateway
      })
      console.log('[Checkout] Response data:', response.data)

      if (response.data && response.data.data) {
        console.log('[Checkout] Response data.data exists:', {
          hasAuthUrl: !!response.data.data.authorizationUrl,
          hasOrdId: !!response.data.data.orderId,
          authUrl: response.data.data.authorizationUrl?.substring(0, 50) + '...'
        })
      } else {
        console.warn('[Checkout] Response data.data is missing or undefined', {
          responseData: response.data,
          dataExists: !!response.data?.data
        })
      }

      if (response.data.data?.authorizationUrl) {
        console.log('[Checkout] Authorization URL found, proceeding with redirect')
        // Clear cart on success
        clearCart()
        
        const authUrl = response.data.data.authorizationUrl
        console.log('[Checkout] Clearing cart and redirecting to:', authUrl)

        // Small delay to ensure cart is cleared
        setTimeout(() => {
          console.log('[Checkout] Executing redirect to payment gateway')
          window.location.href = authUrl
        }, 100)
      } else {
        console.error('[Checkout] Missing authorizationUrl in response:', {
          responseData: response.data,
          dataPath: response.data?.data,
          keys: response.data?.data ? Object.keys(response.data.data) : 'no data object',
          gateway: website?.paymentSettings?.paymentGateway
        })
        throw new Error(`Failed to get payment authorization URL. Response: ${JSON.stringify(response.data)}`)
      }
    } catch (error) {
      console.error('[Checkout] CRITICAL ERROR - Checkout failed:', {
        name: error.name,
        message: error.message,
        code: error.code
      })

      // Log different error types
      if (error.response) {
        console.error('[Checkout] API Error Response:', {
          status: error.response.status,
          statusText: error.response.statusText,
          data: error.response.data
        })
      } else if (error.request) {
        console.error('[Checkout] No response from server:', {
          request: error.request
        })
      } else {
        console.error('[Checkout] Error during request setup:', error.message)
      }

      const errorMsg = 
        error.response?.data?.message || 
        error.response?.data?.details ||
        error.response?.data?.error ||
        error.message || 
        'Payment initialization failed'
      
      console.error('[Checkout] Showing error to user:', errorMsg)
      toast.error(errorMsg)
    } finally {
      setLoading(false)
    }
  }

  // If cart is empty, show message
  if (cartItems.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto">
          <button
            onClick={onBack}
            className="flex items-center gap-2 text-primary-600 hover:text-primary-700 mb-8 font-medium transition-colors"
          >
            <FiArrowLeft className="w-4 h-4" />
            Back to Cart
          </button>

          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <p className="text-gray-600 text-lg">Your cart is empty. Please add items before checkout.</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-primary-600 hover:text-primary-700 mb-8 font-medium transition-colors"
        >
          <FiArrowLeft className="w-4 h-4" />
          Back to Cart
        </button>

        <h1 className="text-4xl font-bold text-gray-900 mb-8">Checkout</h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Checkout Form */}
          <div className="lg:col-span-2">
            <form onSubmit={handleCheckout} className="space-y-6">
              {/* Shipping Information */}
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Shipping Information</h2>

                {/* Full Name */}
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-2" style={labelStyle}>
                    Full Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="John Doe"
                    style={inputStyle}
                    className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 transition ${
                      errors.name
                        ? 'border-red-500 focus:ring-red-500'
                        : 'border-gray-300 focus:ring-primary-500'
                    }`}
                  />
                  {errors.name && (
                    <p className="text-red-600 text-sm mt-1">{errors.name}</p>
                  )}
                </div>

                {/* Email */}
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-2" style={labelStyle}>
                    Email Address *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="john@example.com"
                    style={inputStyle}
                    className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 transition ${
                      errors.email
                        ? 'border-red-500 focus:ring-red-500'
                        : 'border-gray-300 focus:ring-primary-500'
                    }`}
                  />
                  {errors.email && (
                    <p className="text-red-600 text-sm mt-1">{errors.email}</p>
                  )}
                </div>

                {/* Phone */}
                <div>
                  <label className="block text-sm font-medium mb-2" style={labelStyle}>
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+234 (80) 1234 5678"
                    style={inputStyle}
                    className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 transition ${
                      errors.phone
                        ? 'border-red-500 focus:ring-red-500'
                        : 'border-gray-300 focus:ring-primary-500'
                    }`}
                  />
                  {errors.phone && (
                    <p className="text-red-600 text-sm mt-1">{errors.phone}</p>
                  )}
                </div>

                {/* Address */}
                <div className="mt-4">
                  <label className="block text-sm font-medium mb-2" style={labelStyle}>Address *</label>
                  <textarea
                    name="address"
                    value={formData.address || ''}
                    onChange={handleChange}
                    placeholder="Street address, building, suite, etc."
                    style={inputStyle}
                    className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 transition ${
                      errors.address ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-primary-500'
                    }`}
                    rows={3}
                  />
                  {errors.address && (
                    <p className="text-red-600 text-sm mt-1">{errors.address}</p>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                  <div>
                    <label className="block text-sm font-medium mb-2" style={labelStyle}>City *</label>
                    <input
                      type="text"
                      name="city"
                      value={formData.city || ''}
                      onChange={handleChange}
                      placeholder="Lagos"
                      style={inputStyle}
                      className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 transition ${
                        errors.city ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-primary-500'
                      }`}
                    />
                    {errors.city && (
                      <p className="text-red-600 text-sm mt-1">{errors.city}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2" style={labelStyle}>State *</label>
                    <input
                      type="text"
                      name="state"
                      value={formData.state || ''}
                      onChange={handleChange}
                      placeholder="Lagos State"
                      style={inputStyle}
                      className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 transition ${
                        errors.state ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-primary-500'
                      }`}
                    />
                    {errors.state && (
                      <p className="text-red-600 text-sm mt-1">{errors.state}</p>
                    )}
                  </div>
                </div>

                <div className="mt-4">
                  <label className="block text-sm font-medium mb-2" style={labelStyle}>ZIP / Postal Code *</label>
                  <input
                    type="text"
                    name="zip"
                    value={formData.zip || ''}
                    onChange={handleChange}
                    placeholder="100001"
                    style={inputStyle}
                    className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 transition ${
                      errors.zip ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-primary-500'
                    }`}
                  />
                  {errors.zip && (
                    <p className="text-red-600 text-sm mt-1">{errors.zip}</p>
                  )}
                </div>
              </div>

              {/* Order Items Review */}
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Order Review</h2>

                <div className="space-y-4">
                  {cartItems.map((item) => (
                    <div key={item._id} className="flex gap-4 pb-4 border-b border-gray-200 last:border-b-0">
                      {/* Product Image */}
                      <div className="flex-shrink-0 w-16 h-16 bg-gray-100 rounded overflow-hidden">
                        {item.image?.url ? (
                          <img
                            src={item.image.url}
                            alt={item.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-gray-400">
                            📦
                          </div>
                        )}
                      </div>

                      {/* Product Details */}
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-gray-900 truncate">{item.name}</h3>
                        <p className="text-sm text-gray-600">Qty: {item.quantity}</p>
                      </div>

                      {/* Price */}
                      <div className="flex-shrink-0 text-right">
                        <p className="font-semibold text-gray-900">
                          {item.currency} {(item.price * item.quantity).toLocaleString('en-US', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2
                          })}
                        </p>
                        <p className="text-xs text-gray-600">
                          {item.currency} {item.price.toLocaleString('en-US', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2
                          })} each
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading}
                className={`w-full py-3 rounded-lg font-semibold text-white transition-all flex items-center justify-center gap-2`}
                style={buttonStyle}
              >
                {loading && <FiLoader className="w-5 h-5 animate-spin" />}
                {loading ? 'Processing...' : 'Complete Purchase'}
              </button>

              {/* Security Info */}
              <div className="p-4 bg-blue-50 rounded border border-blue-200 text-center">
                <p className="text-sm text-blue-700">
                  🔒 Secure payment powered by {website?.paymentSettings?.paymentGateway === 'stripe' ? 'Stripe' : 'Paystack'}
                </p>
              </div>
            </form>
          </div>

          {/* Order Summary Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-sm p-6 sticky top-4">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Order Summary</h2>

              {/* Items List */}
              <div className="space-y-3 mb-6 pb-6 border-b border-gray-200 max-h-96 overflow-y-auto">
                {cartItems.map((item) => (
                  <div key={item._id} className="flex justify-between text-sm">
                    <span className="text-gray-700">
                      {item.name}
                      <br />
                      <span className="text-xs text-gray-600">x{item.quantity}</span>
                    </span>
                    <span className="font-medium text-gray-900 text-right">
                      {item.currency}
                      <br />
                      {(item.price * item.quantity).toLocaleString('en-US', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })}
                    </span>
                  </div>
                ))}
              </div>

              {/* Price Breakdown */}
              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-700">Subtotal</span>
                  <span className="font-medium text-gray-900">
                    {cartItems[0]?.currency || 'NGN'} {subtotal.toLocaleString('en-US', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })}
                  </span>
                </div>

                <div className="flex justify-between text-sm">
                  <span className="text-gray-700">Tax</span>
                  <span className="font-medium text-gray-900 italic text-green-600">Tax Included</span>
                </div>

                <div className="flex justify-between pt-3 border-t-2 border-gray-200">
                  <span className="font-bold text-gray-900">Total</span>
                  <span className="text-2xl font-bold text-primary-600">
                    {cartItems[0]?.currency || 'NGN'} {total.toLocaleString('en-US', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })}
                  </span>
                </div>
              </div>

              {/* Info Box */}
              <div className="mt-6 p-3 bg-green-50 rounded border border-green-200">
                <p className="text-xs text-green-700">
                  ✓ {cartItems.length} item{cartItems.length !== 1 ? 's' : ''} in your order
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
